import { formatPeriods } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import { includesPeriod } from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import {
    ResolvedStatementVerdict,
    VERDICT_MESSAGES,
} from 'modules/common/components/VerifiedStatementUpload/utils/statementVerdict';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

/**
 * Where one uploaded file belongs in the modal.
 *
 * - `slot`      — the file fills the period row(s) it owns: green check, "Matched · <filename>".
 * - `unmatched` — its own row under "Files we couldn't match (n)", with its reason.
 * - `hidden`    — no row anywhere.
 *
 * Ownership decides this, not the verdict kind: a file that holds a required month is in its
 * slot, and a file that holds none is in the couldn't-match list whatever the reason. That is
 * why `notNeeded` and `unmatched` land there alongside the outright failures — the old layout
 * could render them as silent stacked rows because every file had a row, but here the only
 * rows are period slots and the couldn't-match list. A file routed nowhere would be invisible,
 * and an invisible upload is indistinguishable from one that never landed.
 *
 * `hidden` is therefore reachable only while something else accounts for the file: a file
 * still in flight, which the dropzone counts in "Processing N files…".
 */
type StatementRowDestination = 'slot' | 'unmatched' | 'hidden';

interface StatementRowPlacement {
    /**
     * How the row should read.
     *
     * Not every file in the couldn't-match list failed. `notNeeded` is a valid statement for a
     * month outside the window and `unmatched` is a valid statement that lost its slot to
     * another file — painting either red reports a non-failure as a failure, which the layout
     * this replaced avoided by rendering them neutral and info respectively.
     *
     * A `slot` row is tinted by whether its month is filled, not by this, so `success` here is
     * only ever the reading of the file itself.
     */
    tone: 'neutral' | 'info' | 'success' | 'error';
    destination: StatementRowDestination;
    /** The reason, for the couldn't-match list. Absent for the other destinations. */
    message?: string;
    /**
     * Whether the row offers the month menu.
     *
     * True for every row that renders, which is the feature's escape hatch: the member can
     * always name the month, whatever the server made of the file. Worth stating because two
     * narrower rules are tempting and both are wrong — withholding it from a filed file means
     * a correctly filed month can never be moved, and withholding it from a file that failed
     * to read means the member cannot rescue a statement our extraction choked on. The design
     * mocks show delete alone on some of these rows; the invariant outranks the mock.
     *
     * False only where no row renders at all; for an expired file, where the honest move is
     * delete and re-upload: nothing ever read it, so any month named for it would be credited on
     * no evidence; and for a file that doesn't match this card but is already filed on another
     * account, which the server refuses to file a second time.
     */
    canAssignPeriod: boolean;
}

/**
 * Refuses to compile when a verdict kind has no row treatment.
 *
 * The runtime return exists only because a version-skewed server can send a status this build
 * has never heard of; `toStatementVerdict` maps that to `processing`, so it never reaches here
 * as an unknown kind. The value of this function is entirely at compile time.
 */
function assertNever(kind: never): never {
    throw new Error(`Unhandled statement verdict kind: ${String(kind)}`);
}

/**
 * What a file holding no month should say.
 *
 * Never comes from the server — losing a slot is a fact about the set of uploads and about
 * corrections the member has staged, so the copy is derived here. The month-less wording
 * covers the rarer route in: a stored correction naming a month no longer being asked for.
 */
function unmatchedMessage(believedPeriods: StatementPeriod[], requiredPeriods: StatementPeriod[]): string {
    if (!believedPeriods.length) {
        return "This isn't matched to a month.";
    }
    // Three ways to hold no month, and only one of them is a contest. A file we read to a month
    // nobody here asked for lost nothing to anybody — blaming another file for it is simply
    // untrue, and it sends the member hunting for a duplicate that does not exist.
    //
    // Reachable for a file holding no month whose *reading* names one outside the ask — an
    // upload meant for a different card, or a stored correction naming a month no longer asked.
    const isAskedFor = believedPeriods.some((period) => includesPeriod(requiredPeriods, period));
    if (!isAskedFor) {
        return `We think this is your ${formatPeriods(
            believedPeriods
        )} statement, which isn't a month we're asking for here.`;
    }
    return `We think this is your ${formatPeriods(
        believedPeriods
    )} statement, but another file is matched to that month.`;
}

/**
 * Route one file's resolved verdict.
 *
 * `isExpired` is passed separately rather than read off the verdict because an expired file is
 * still `processing` by kind. It is the one processing file that must not be hidden: the poll
 * has stopped, so nothing is feeding the dropzone's counter, and a hidden row would be a file
 * the member can neither see nor delete.
 */
export default function routeStatementRow(
    verdict: ResolvedStatementVerdict | undefined,
    isExpired: boolean,
    requiredPeriods: StatementPeriod[]
): StatementRowPlacement {
    // Ahead of everything else: expiry contradicts the kind it is derived from.
    if (isExpired) {
        return { destination: 'unmatched', tone: 'error', message: VERDICT_MESSAGES.expired, canAssignPeriod: false };
    }

    // A file the server has not spoken about yet reads the same as one it says is processing.
    // Handled before the switch so the branches below can rely on having a verdict.
    if (!verdict) {
        return { destination: 'hidden', tone: 'neutral', canAssignPeriod: false };
    }

    switch (verdict.kind) {
        case 'matched':
            return { destination: 'slot', tone: 'success', canAssignPeriod: true };
        case 'processing':
            return { destination: 'hidden', tone: 'neutral', canAssignPeriod: false };
        // Accepted, but holding no month — it states what we think it is without claiming credit.
        case 'unmatched':
            return {
                destination: 'unmatched',
                tone: 'info',
                message: unmatchedMessage(verdict.believedPeriods, requiredPeriods),
                canAssignPeriod: true,
            };
        // A valid statement whose month nobody asked for. Nothing is wrong with the file and
        // there is nothing to do about it, so it says so without being painted as a failure.
        case 'notNeeded':
            return { destination: 'unmatched', tone: 'neutral', message: verdict.message, canAssignPeriod: true };
        // On another card's list, and holding nothing here. Not wrong, just not this card's —
        // and the menu stays, since placing it here is the member's call.
        case 'filedElsewhere':
            return { destination: 'unmatched', tone: 'neutral', message: verdict.message, canAssignPeriod: true };
        // Read fine, and the month it names is already covered — the member has it. Not a failure,
        // and the menu stays: taking the month over on this card is still theirs to do.
        case 'alreadyHeld':
            return { destination: 'unmatched', tone: 'info', message: verdict.message, canAssignPeriod: true };
        // Still a refusal on this card. The menu (the "really is this account" override) goes
        // only where the server would refuse it: the statement is already filed on another account.
        case 'wrongAccount':
            return {
                destination: 'unmatched',
                tone: 'error',
                message: verdict.message,
                canAssignPeriod: !verdict.isFiledOnAnotherAccount,
            };
        // Real failures: all hold no month, all keep the escape hatch, all arrive with a reason.
        case 'error':
        case 'notAStatement':
        case 'noPeriod':
        case 'unfiled':
            return { destination: 'unmatched', tone: 'error', message: verdict.message, canAssignPeriod: true };
        default:
            // Enumerated rather than defaulted, so a new verdict kind is a compile error here
            // instead of a silent one. A `default:` arm gave every future kind the generic
            // unmatched treatment, which is a plausible answer for some and wrong for any that
            // needs its own remediation — and nothing would have forced anyone to look.
            return assertNever(verdict.kind);
    }
}
